import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Draft,
  DraftAttachment,
  DraftList,
  DraftDeferredSendResponse,
  DraftSendAcceptedResponse,
  type DraftCreate,
  type DraftFromMessageCreate,
  type DraftImportCreate,
  type DraftPatch,
  type DraftSendRequest,
  type DraftScheduleRequest,
} from '@email/shared';
import { ApiError, api } from './client.js';
import { mailInvalidations, mailKeys } from './query-cache.js';

export function useDrafts(enabled = true) {
  return useQuery({
    queryKey: mailKeys.drafts,
    queryFn: () => api('/api/drafts', { schema: DraftList }),
    enabled,
  });
}

export function useCreateDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftCreate) =>
      api('/api/drafts', { method: 'POST', body, schema: Draft }),
    onSuccess: (created) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.some((draft) => draft.id === created.id)
          ? current.map((draft) => (draft.id === created.id ? created : draft))
          : [...current, created],
      );
    },
  });
}

export function useCreateDraftFromMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftFromMessageCreate) =>
      api('/api/drafts/from-message', { method: 'POST', body, schema: Draft }),
    onSuccess: (created) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.some((draft) => draft.id === created.id) ? current : [...current, created],
      );
    },
    onSettled: () => mailInvalidations.drafts.invalidate(queryClient),
  });
}

export function useImportProviderDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftImportCreate) =>
      api('/api/drafts/import-message', { method: 'POST', body, schema: Draft }),
    onSuccess: (created) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.some((draft) => draft.id === created.id) ? current : [...current, created],
      );
    },
    onSettled: () => mailInvalidations.drafts.invalidate(queryClient),
  });
}

export function useRetryDraftSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/drafts/${id}/sync`, { method: 'POST', schema: Draft }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) => (draft.id === updated.id ? updated : draft)),
      );
    },
  });
}

export function usePatchDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DraftPatch }) =>
      api(`/api/drafts/${id}`, { method: 'PATCH', body, schema: Draft }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) => (draft.id === updated.id ? updated : draft)),
      );
    },
  });
}

export function useUploadDraftAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadDraftAttachment,
    onSuccess: (attachment, variables) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) =>
          draft.id === variables.draftId &&
          !draft.attachments.some((existing) => existing.id === attachment.id)
            ? { ...draft, attachments: [...draft.attachments, attachment] }
            : draft,
        ),
      );
    },
    onSettled: () => mailInvalidations.drafts.invalidate(queryClient),
  });
}

export function useDeleteDraftAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, attachmentId }: { draftId: number; attachmentId: number }) =>
      api(`/api/drafts/${draftId}/attachments/${attachmentId}`, { method: 'DELETE' }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) =>
          draft.id === variables.draftId
            ? {
                ...draft,
                attachments: draft.attachments.filter(
                  (attachment) => attachment.id !== variables.attachmentId,
                ),
              }
            : draft,
        ),
      );
    },
    onSettled: () => mailInvalidations.drafts.invalidate(queryClient),
  });
}

export function useDeleteDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/drafts/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.filter((draft) => draft.id !== id),
      );
    },
    onSettled: () => mailInvalidations.draftsAndSync.invalidate(queryClient),
  });
}

export function useSendDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DraftSendRequest }) =>
      api(`/api/drafts/${id}/send`, {
        method: 'POST',
        body,
        schema: DraftSendAcceptedResponse,
        // Older staged PWA bundles do not understand the deferred response.
        // The server sends immediately unless the active client opts in.
        headers: { 'x-wintermute-deferred-send': '1' },
      }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        'state' in result && result.state === 'scheduled'
          ? current.map((draft) => (draft.id === result.draft.id ? result.draft : draft))
          : current.filter((draft) => draft.id !== variables.id),
      );
    },
    onSettled: () => mailInvalidations.draftsAndSync.invalidate(queryClient),
  });
}

export function useScheduleDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DraftScheduleRequest }) =>
      api(`/api/drafts/${id}/schedule`, {
        method: 'POST',
        body,
        schema: DraftDeferredSendResponse,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) => (draft.id === result.draft.id ? result.draft : draft)),
      );
    },
    onSettled: () => mailInvalidations.draftsAndSync.invalidate(queryClient),
  });
}

export function useCancelScheduledDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/drafts/${id}/cancel-send`, { method: 'POST', schema: Draft }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Draft[]>(mailKeys.drafts, (current = []) =>
        current.map((draft) => (draft.id === updated.id ? updated : draft)),
      );
    },
    onSettled: () => mailInvalidations.draftsAndSync.invalidate(queryClient),
  });
}

interface UploadDraftAttachmentInput {
  draftId: number;
  file: File;
  inline?: boolean;
  onProgress?: (percent: number) => void;
}

function uploadDraftAttachment({
  draftId,
  file,
  inline,
  onProgress,
}: UploadDraftAttachmentInput): Promise<DraftAttachment> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `/api/drafts/${draftId}/attachments${inline ? '?inline=1' : ''}`);
    request.withCredentials = true;
    request.timeout = 5 * 60_000;
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    });
    request.addEventListener('error', () => reject(new Error('Attachment upload failed.')));
    request.addEventListener('abort', () => reject(new Error('Attachment upload was cancelled.')));
    request.addEventListener('timeout', () => reject(new Error('Attachment upload timed out.')));
    request.addEventListener('load', () => {
      const body = parseResponseBody(request.responseText);
      if (request.status < 200 || request.status >= 300) {
        const message =
          body !== null && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : `Attachment upload failed (${request.status})`;
        reject(new ApiError(request.status, message, body));
        return;
      }
      try {
        resolve(DraftAttachment.parse(body));
      } catch (error) {
        reject(error);
      }
    });

    const data = new FormData();
    data.append('attachment', file, file.name);
    request.send(data);
  });
}

function parseResponseBody(raw: string): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}
